import { Button } from "primereact/button";
import type TooltipOptions from "primereact/tooltip/tooltipoptions";
import { useCallback } from "react";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type createExternalLicenseRewardButton_AddExternalLicenseRewardMutation } from "@relay/createExternalLicenseRewardButton_AddExternalLicenseRewardMutation.graphql";
import { type createExternalLicenseRewardButton_ExternalLicensePoolsConnectionFragment$key } from "@relay/createExternalLicenseRewardButton_ExternalLicensePoolsConnectionFragment.graphql";
import { type createExternalLicenseRewardButton_TreeNodeFragment$key } from "@relay/createExternalLicenseRewardButton_TreeNodeFragment.graphql";
import {
	ADD_EXTERNAL_LICENSE_REWARD_MUTATION,
	EXTERNAL_LICENSE_POOL_CONNECTION_FRAGMENT,
	TREE_NODE_FRAGMENT,
} from "./create-external-license-reward-button.graphql";
import { type CreateExternalLicenseRewardButtonProps } from "./create-external-license-reward-button.types";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const CreateExternalLicenseRewardButton = ({
	treeNodeFragmentRef,
	externalLicensePoolConnectionFragmentRef,
}: CreateExternalLicenseRewardButtonProps) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const node = useFragment<createExternalLicenseRewardButton_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const externalLicensePoolConnection =
		useFragment<createExternalLicenseRewardButton_ExternalLicensePoolsConnectionFragment$key>(
			EXTERNAL_LICENSE_POOL_CONNECTION_FRAGMENT,
			externalLicensePoolConnectionFragmentRef,
		);
	const [addExternalLicenseReward, isAddingExternalLicense] =
		useMutation<createExternalLicenseRewardButton_AddExternalLicenseRewardMutation>(
			ADD_EXTERNAL_LICENSE_REWARD_MUTATION,
		);

	const noExternalLicensePools = externalLicensePoolConnection.edges?.length === 0;
	const tooltip = noExternalLicensePools
		? t("educationalOffer.noPoolExternalLicence")
		: t("externalLicenses.assignExternalLicence");
	const disabled = isAddingExternalLicense || noExternalLicensePools;
	const tooltipOptions: TooltipOptions.TooltipOptions = { showOnDisabled: true, position: "top" };
	const handleOnClick = useCallback(() => {
		const poolId = externalLicensePoolConnection?.edges?.map((e) => e?.node).shift()?.id;
		if (!poolId) return;

		addExternalLicenseReward({
			variables: {
				input: {
					contentId: node.id,
					data: {
						pool: poolId,
					},
				},
			},
			updater: setDraft,
		});
	}, [externalLicensePoolConnection, node, addExternalLicenseReward]);

	return (
		<Button
			className={"mr-2"}
			disabled={disabled}
			tooltipOptions={tooltipOptions}
			tooltip={tooltip}
			icon="pi pi-key"
			onClick={handleOnClick}
		/>
	);
};
